import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, ConfirmationNumberOutlined as TicketIcon } from '@mui/icons-material';
import { useSnackbar } from './SnackbarService';

interface RequestToolAccessModalProps {
  open: boolean;
  onClose: () => void;
  toolName: string;
}

export const RequestToolAccessModal: React.FC<RequestToolAccessModalProps> = ({
  open,
  onClose,
  toolName,
}) => {
  const { showSnackbar, snackbarType } = useSnackbar();
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    showSnackbar({
      message: `Access ticket requested for ${toolName}. Our team will review your request.`,
      type: snackbarType.SUCCESS,
    });
    setTimeout(() => {
      setSubmitted(false);
      setReason('');
      onClose();
    }, 1200);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0F0F0F',
          border: '1px solid #222',
          borderRadius: '14px',
          color: '#fff',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #1F1F1F',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TicketIcon sx={{ color: '#10B981', fontSize: 20 }} />
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: '#fff' }}>
            Request Access — {toolName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#777', '&:hover': { color: '#fff' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          {submitted ? (
            <Alert severity="success" sx={{ bgcolor: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.3)' }}>
              Ticket submitted successfully! Our support team will process tool provisioning for your organisation.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#AAA', lineHeight: 1.5 }}>
                This tool is not currently enabled for your organisation or project. Submitting this request will open an access request ticket with Darukaa platform administrators.
              </Typography>

              <TextField
                label="Tool Requested"
                value={toolName}
                fullWidth
                disabled
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                sx={{
                  bgcolor: '#161616',
                  borderRadius: '8px',
                  '& .MuiInputBase-input': { color: '#10B981', fontWeight: 700 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                }}
              />

              <TextField
                label="Business Justification / Project Requirements (Optional)"
                multiline
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain why your project requires access to this tool..."
                fullWidth
                sx={{
                  bgcolor: '#161616',
                  borderRadius: '8px',
                  '& .MuiInputBase-input': { color: '#FFF' },
                  '& .MuiInputLabel-root': { color: '#777' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#10B981' },
                }}
              />
            </Box>
          )}
        </DialogContent>

        {!submitted && (
          <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #1F1F1F' }}>
            <Button onClick={onClose} sx={{ color: '#777', fontWeight: 700 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: '#10B981', color: '#001D12', fontWeight: 900, '&:hover': { bgcolor: '#059669' } }}
            >
              Submit Ticket
            </Button>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

export default RequestToolAccessModal;
